import { Grid } from '@mui/material';
import { RoutalDialog } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { GeoFence } from '../../../../../domain/geoFence/GeoFence';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useUpdateProjectGeoFences } from '../../../../../hooks/project/useUpdateProjectGeoFences';
import { useRetrieveUserCredentials } from '../../../../../hooks/user/useRetrieveUserCredentials';
import Button from '../../../../../react-components/Button';
import Text from '../../../../../react-components/Text';
import { GeoFencesDialogList } from './GeoFencesDialogList';
import { GeoFencesDialogMap } from './GeoFencesDialogMap';

type GeoFencesDialogOverviewProps = {
  dialogOpen: boolean;
  onCloseDialog: () => any;
};

export const GeoFencesDialogOverview = ({ dialogOpen, onCloseDialog }: GeoFencesDialogOverviewProps) => {
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: geoFences } = useRetrieveActiveProjectGeoFences();

  const { run: updateProjectGeoFences, success, isLoading } = useUpdateProjectGeoFences();

  const { data: activeProject } = useRetrieveActiveProject();

  const [selectedGeoFenceIds, setSelectedGeoFenceIds] = useState<string[]>([]);
  useEffect(() => {
    const activeGeoFencesIds =
      geoFences?.filter((geoFence) => geoFence.enabled).map((geoFence) => geoFence.id.toString()) ?? [];
    setSelectedGeoFenceIds(activeGeoFencesIds);
  }, [geoFences]);

  useEffect(() => {
    if (success) {
      handleOnCloseDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const handleOnCloseDialog = () => {
    // Before closing the dialog, we need to reset the selected zones
    const activeGeoFencesIds = geoFences?.filter((geo) => geo.enabled).map((geoFence) => geoFence.id.toString()) ?? [];
    setSelectedGeoFenceIds(activeGeoFencesIds);
    onCloseDialog();
  };

  const [highlightedGeoFenceId, setHighlightedGeoFenceId] = useState<string | undefined>(undefined);
  const [isDrawingGeoFence, setIsDrawingGeoFence] = useState<boolean>(false);
  const [editingGeoFence, setEditingGeoFence] = useState<GeoFence | undefined>(undefined);

  if (!activeProject || !userCredentials) return null;

  if (!userCredentials.subscription.test(`planner.geofences`)) {
    return null;
  }

  return (
    <RoutalDialog
      open={dialogOpen}
      dialogContentWarning={<div />}
      onCloseDialog={handleOnCloseDialog}
      titleContent={<Text variant="h6" intlId="update_geo_fences_dialog.title" />}
      dialogProps={{
        maxWidth: `xl`,
      }}
      contentStyles={{
        padding: `0`,
      }}
      footerContent={
        <Button
          // disabled={}
          disabled={isLoading}
          id="change_status"
          styleType="primary"
          onClick={() => {
            const selectedZones = geoFences?.filter((geoFence) => selectedGeoFenceIds.includes(geoFence.id));
            (selectedZones ?? []).forEach((geoFence) => {
              geoFence.enabled = true;
            });
            const nonSelectedZones = geoFences?.filter((geoFence) => !selectedGeoFenceIds.includes(geoFence.id));
            (nonSelectedZones ?? []).forEach((geoFence) => {
              geoFence.enabled = false;
            });
            if (!(selectedZones && nonSelectedZones) || isLoading) return;
            updateProjectGeoFences({ geoFences: [...selectedZones, ...nonSelectedZones] });
          }}
          intlId="update_geo_fences_dialog.button"
          style={{ userSelect: `none` }}
        />
      }
    >
      <Grid container direction="row" wrap="nowrap" style={{ height: `780px` }}>
        <GeoFencesDialogList
          project={activeProject}
          geoFences={geoFences ?? []}
          selectedGeoFenceIds={selectedGeoFenceIds}
          isDrawing={isDrawingGeoFence || editingGeoFence !== undefined}
          setEditingGeoFence={setEditingGeoFence}
          setSelectedGeoFenceIds={setSelectedGeoFenceIds}
          setHighlightedGeoFenceId={setHighlightedGeoFenceId}
          isLoading={isLoading}
        />

        <GeoFencesDialogMap
          project={activeProject}
          geoFences={geoFences ?? []}
          selectedGeoFenceIds={selectedGeoFenceIds}
          highlightedGeoFenceId={highlightedGeoFenceId}
          editingGeoFence={editingGeoFence}
          enableRightPanel={false}
          setIsDrawingGeoFence={setIsDrawingGeoFence}
          setEditingGeoFence={setEditingGeoFence}
          setSelectedGeoFenceIds={setSelectedGeoFenceIds}
          setHighlightedGeoFenceId={setHighlightedGeoFenceId}
        />
      </Grid>
    </RoutalDialog>
  );
};
